// Templates de tema (spec-templates-tema.md, Fase 2).
// Um Template = pacote completo de cores (os 36 tokens de tema) + extras (acento,
// destaque, linha). Tipografia e global (styles.css), igual nos dois modos.
//
// Built-in: cobre_claro e cobre_escuro. O "personalizado" (legado) NAO vive aqui —
// e o que estiver salvo em configuracoes_ui (GET /api/configuracoes/cores), aplicado
// pelo ThemeProvider como fallback de compatibilidade.

import { aplicarTemaNoRoot, type ChaveTema } from './tema';

export type ModoTema = 'claro' | 'escuro';

export interface Template {
  id: string;
  nome: string;
  descricao: string;
  modo: ModoTema;
  /** Os 36 tokens de tema (CHAVES_TEMA). */
  cores: Record<ChaveTema, string>;
  /** CSS vars extras (acento, destaque, linha) aplicadas direto no :root. */
  extras: Record<string, string>;
}

// ───────────────────────────── Cobre Claro ─────────────────────────────
const COBRE_CLARO: Template = {
  id: 'cobre_claro',
  nome: 'Cobre Claro',
  descricao: 'Claro, neutros quentes e acento cobre. Padrao da casa.',
  modo: 'claro',
  cores: {
    page_bg: '#f3ede3',
    page_fg: '#1c1410',
    panel_bg: '#ffffff',
    panel_fg: '#1c1410',
    app_header_bg: 'linear-gradient(180deg, #c2410c 0%, #9a3412 46%, #7c2d12 100%)',
    app_header_fg: '#fff8ef',
    page_header_bg: '#ffffff',
    page_header_fg: '#1c1410',
    modal_bg: '#ffffff',
    modal_fg: '#1c1410',
    modal_header_bg: '#faf7f2',
    modal_header_fg: '#1c1410',
    card_bg: '#ffffff',
    card_fg: '#1c1410',
    input_bg: '#ffffff',
    input_fg: '#1c1410',
    input_border: '#d6c8b2',
    popup_bg: '#ffffff',
    popup_fg: '#1c1410',
    popup_border: '#e2d6c4',
    grid_header_bg: '#1c1410',
    grid_header_fg: '#f6e6cf',
    grid_subheader_bg: '#3a302a',
    grid_subheader_fg: '#f6e6cf',
    grid_row_bg: '#ffffff',
    grid_row_alt_bg: '#faf7f2',
    grid_row_fg: '#1c1410',
    grid_row_hover_bg: '#fbeedd',
    wa_sidebar_bg: '#1c1410',
    wa_chat_header_bg: '#2a1d14',
    wa_chat_bg: '#ece5d8',
    wa_bubble_out_bg: '#fde68a',
    wa_text_fg: '#e7e0d6',
    wa_subtext_fg: '#a18a6f',
    tabs_bg: '#f3ede3',
    tabs_active_fg: '#b45309',
  },
  extras: {
    '--accent': '#b45309',
    '--accent-fg': '#fff8ef',
    '--accent-soft': 'rgba(180, 83, 9, 0.12)',
    '--positive-bg': '#fde68a',
    '--positive-fg': '#000000',
    '--line': '#e2d6c4',
    '--border': '#e2d6c4',
    '--primary': '#b45309',
    '--primary-hover': '#9a3412',
    '--primary-soft': '#fbeedd',
    '--menu-ativo-bg': '#fbeedd',
    '--menu-ativo-text': '#9a3412',
    '--menu-hover': '#f3ede3',
    '--text-muted': '#7a6f5e',
  },
};

// ──────────────────────────── Cobre Escuro ─────────────────────────────
const COBRE_ESCURO: Template = {
  id: 'cobre_escuro',
  nome: 'Cobre Escuro',
  descricao: 'Escuro premium (Midnight), acento ambar. Conforto a noite.',
  modo: 'escuro',
  cores: {
    page_bg: '#0a0c10',
    page_fg: '#e7e9ee',
    panel_bg: '#13161d',
    panel_fg: '#e7e9ee',
    app_header_bg: 'linear-gradient(180deg, #161a22 0%, #0e1117 52%, #090b0f 100%)',
    app_header_fg: '#e7e9ee',
    page_header_bg: '#13161d',
    page_header_fg: '#e7e9ee',
    modal_bg: '#13161d',
    modal_fg: '#e7e9ee',
    modal_header_bg: '#0f1218',
    modal_header_fg: '#e7e9ee',
    card_bg: '#13161d',
    card_fg: '#e7e9ee',
    input_bg: '#0f131a',
    input_fg: '#e7e9ee',
    input_border: '#2a2f3a',
    popup_bg: '#13161d',
    popup_fg: '#e7e9ee',
    popup_border: '#222732',
    grid_header_bg: '#0f1218',
    grid_header_fg: '#aeb3bd',
    grid_subheader_bg: '#181c24',
    grid_subheader_fg: '#aeb3bd',
    grid_row_bg: '#13161d',
    grid_row_alt_bg: '#15191f',
    grid_row_fg: '#e7e9ee',
    grid_row_hover_bg: '#1b2029',
    wa_sidebar_bg: '#0d1016',
    wa_chat_header_bg: '#13161d',
    wa_chat_bg: '#0a0c10',
    wa_bubble_out_bg: '#fde68a',
    wa_text_fg: '#e7e9ee',
    wa_subtext_fg: '#8a909c',
    tabs_bg: '#0f1218',
    tabs_active_fg: '#f5a623',
  },
  extras: {
    '--accent': '#f5a623',
    '--accent-fg': '#0a0c10',
    '--accent-soft': 'rgba(245, 166, 35, 0.14)',
    '--positive-bg': '#fbbf24',
    '--positive-fg': '#0a0c10',
    '--line': '#222732',
    '--border': '#222732',
    '--primary': '#f5a623',
    '--primary-hover': '#fbbf24',
    '--primary-soft': 'rgba(245, 166, 35, 0.14)',
    '--menu-ativo-bg': 'rgba(245, 166, 35, 0.14)',
    '--menu-ativo-text': '#f5a623',
    '--menu-hover': 'rgba(255, 255, 255, 0.05)',
    '--text-muted': '#8a909c',
  },
};

// ─────────────────────────── Azul Escuro ────────────────────────────────
// Par escuro do Azul Royal — mesmo espírito "Midnight" do Cobre Escuro,
// com tonalidade azul-marinho nos fundos e azul royal como acento.
// Mantém destaque positivo amarelo+preto (daltonismo) e bolha WA amarela.
const AZUL_ESCURO: Template = {
  id: 'azul_escuro',
  nome: 'Azul Escuro',
  descricao: 'Escuro Midnight com tonalidade azul-marinho e acento azul royal.',
  modo: 'escuro',
  cores: {
    page_bg: '#080c14',
    page_fg: '#dce6f7',
    panel_bg: '#0d1422',
    panel_fg: '#dce6f7',
    app_header_bg: 'linear-gradient(180deg, #0f1c38 0%, #091226 52%, #050c1c 100%)',
    app_header_fg: '#dce6f7',
    page_header_bg: '#0d1422',
    page_header_fg: '#dce6f7',
    modal_bg: '#0d1422',
    modal_fg: '#dce6f7',
    modal_header_bg: '#0a1020',
    modal_header_fg: '#dce6f7',
    card_bg: '#0d1422',
    card_fg: '#dce6f7',
    input_bg: '#0a1120',
    input_fg: '#dce6f7',
    input_border: '#1e2e4e',
    popup_bg: '#0d1422',
    popup_fg: '#dce6f7',
    popup_border: '#192340',
    grid_header_bg: '#091020',
    grid_header_fg: '#7eadee',
    grid_subheader_bg: '#0d172e',
    grid_subheader_fg: '#7eadee',
    grid_row_bg: '#0d1422',
    grid_row_alt_bg: '#0f1828',
    grid_row_fg: '#dce6f7',
    grid_row_hover_bg: '#132040',
    wa_sidebar_bg: '#07101e',
    wa_chat_header_bg: '#0d1422',
    wa_chat_bg: '#080c14',
    wa_bubble_out_bg: '#fde68a',
    wa_text_fg: '#dce6f7',
    wa_subtext_fg: '#5878a8',
    tabs_bg: '#0a1020',
    tabs_active_fg: '#4d8ef0',
  },
  extras: {
    '--accent': '#2563eb',
    '--accent-fg': '#eff6ff',
    '--accent-soft': 'rgba(37, 99, 235, 0.16)',
    '--positive-bg': '#fbbf24',
    '--positive-fg': '#080c14',
    '--line': '#192340',
    '--border': '#192340',
    '--primary': '#4d8ef0',
    '--primary-hover': '#6ba3f5',
    '--primary-soft': 'rgba(77, 142, 240, 0.16)',
    '--menu-ativo-bg': 'rgba(77, 142, 240, 0.16)',
    '--menu-ativo-text': '#4d8ef0',
    '--menu-hover': 'rgba(255, 255, 255, 0.05)',
    '--text-muted': '#5878a8',
  },
};

// ───────────────────────── Azul Royal (exemplo) ─────────────────────────
// Template de avaliacao (Ruan, 2026-05-25): paleta predominante azul, claro.
// Mesma estrutura dos Cobre — neutros levemente frios (azulados), cabecalho
// azul em degrade, acento azul royal. Mantem o destaque positivo amarelo+preto
// (regra daltonismo) e a bolha de WhatsApp amarela por consistencia.
const AZUL_CLARO: Template = {
  id: 'azul_claro',
  nome: 'Azul Royal',
  descricao: 'Claro, neutros frios e acento azul royal. Modelo pra avaliacao.',
  modo: 'claro',
  cores: {
    page_bg: '#96b3e8',
    page_fg: '#0f1d3a',
    panel_bg: '#cfdef6',
    panel_fg: '#0f1d3a',
    app_header_bg: 'linear-gradient(180deg, #2563eb 0%, #1d4ed8 46%, #1e3a8a 100%)',
    app_header_fg: '#eff6ff',
    page_header_bg: '#94b5ea',
    page_header_fg: '#0f1d3a',
    modal_bg: '#cfdef6',
    modal_fg: '#0f1d3a',
    modal_header_bg: '#94b5ea',
    modal_header_fg: '#0f1d3a',
    card_bg: '#cfdef6',
    card_fg: '#0f1d3a',
    input_bg: '#ffffff',
    input_fg: '#0f1d3a',
    input_border: '#c3d4f0',
    popup_bg: '#ffffff',
    popup_fg: '#0f1d3a',
    popup_border: '#d8e3f7',
    grid_header_bg: '#11224a',
    grid_header_fg: '#cfe0ff',
    grid_subheader_bg: '#1e3566',
    grid_subheader_fg: '#cfe0ff',
    grid_row_bg: '#cfdef6',
    grid_row_alt_bg: '#bdd2ef',
    grid_row_fg: '#0f1d3a',
    grid_row_hover_bg: '#aecbf3',
    wa_sidebar_bg: '#11224a',
    wa_chat_header_bg: '#1a2c54',
    wa_chat_bg: '#e4ebf5',
    wa_bubble_out_bg: '#fde68a',
    wa_text_fg: '#dde6f2',
    wa_subtext_fg: '#93a4c0',
    tabs_bg: '#96b3e8',
    tabs_active_fg: '#1d4ed8',
  },
  extras: {
    '--accent': '#1d4ed8',
    '--accent-fg': '#eff6ff',
    '--accent-soft': 'rgba(29, 78, 216, 0.12)',
    '--positive-bg': '#fde68a',
    '--positive-fg': '#000000',
    '--line': '#d8e3f7',
    '--border': '#d8e3f7',
    '--primary': '#1d4ed8',
    '--primary-hover': '#1e40af',
    '--primary-soft': '#e1ecfd',
    '--menu-ativo-bg': '#e1ecfd',
    '--menu-ativo-text': '#1e40af',
    '--menu-hover': '#eaf0fb',
    '--text-muted': '#5c6b8a',
  },
};

export const TEMPLATES: Template[] = [COBRE_CLARO, COBRE_ESCURO, AZUL_CLARO, AZUL_ESCURO];

export const TEMPLATE_PADRAO_ID = 'cobre_claro';

// Extras do tema "Personalizado (legado)" — restaura o visual classico (azul)
// pra quando a casa ainda nao migrou ou o usuario volta pro tema antigo.
// MESMO conjunto de chaves dos templates cobre (pra sobrescrever 100% ao trocar).
export const EXTRAS_LEGADO: Record<string, string> = {
  '--accent': '#b45309',
  '--accent-fg': '#fff8ef',
  '--accent-soft': 'rgba(180, 83, 9, 0.12)',
  '--positive-bg': '#fde68a',
  '--positive-fg': '#000000',
  '--line': '#e5e7eb',
  '--border': '#e5e7eb',
  '--primary': '#2563eb',
  '--primary-hover': '#1d4ed8',
  '--primary-soft': '#dbeafe',
  '--menu-ativo-bg': '#dbeafe',
  '--menu-ativo-text': '#1d4ed8',
  '--menu-hover': '#f3f4f6',
  '--text-muted': '#6b7280',
};

/** Aplica cores + extras do tema "personalizado (legado)" (cores vindas de /cores). */
export function aplicarLegado(cores: Record<ChaveTema, string>): void {
  aplicarTemaNoRoot(cores);
  const root = document.documentElement;
  for (const [k, v] of Object.entries(EXTRAS_LEGADO)) root.style.setProperty(k, v);
  root.setAttribute('data-modo', 'claro');
  root.setAttribute('data-tema', 'personalizado');
}

export function templatePorId(id: string | null | undefined): Template | null {
  if (!id) return null;
  return TEMPLATES.find((t) => t.id === id) ?? null;
}

export function templatePorModo(modo: ModoTema): Template {
  return modo === 'escuro' ? COBRE_ESCURO : COBRE_CLARO;
}

/**
 * Dado o template atual e o modo destino, devolve o "par natural" do template.
 * Cobre Claro <-> Cobre Escuro, Azul Royal <-> Azul Escuro.
 * Para `personalizado` (só existe no claro) não há par escuro — retorna null,
 * deixando o chamador decidir (cair em templatePorModo ou slot lembrado).
 */
export function parDoTemplate(template: Template | null, modoDestino: ModoTema): Template | null {
  if (!template) return null;
  const pares: Record<string, string> = {
    cobre_claro: 'cobre_escuro',
    cobre_escuro: 'cobre_claro',
    azul_claro: 'azul_escuro',
    azul_escuro: 'azul_claro',
  };
  const parId = pares[template.id];
  if (!parId) return null;
  const par = templatePorId(parId);
  // Só retorna o par se o modo bate com o destino.
  return par && par.modo === modoDestino ? par : null;
}

/** Aplica um template completo no documento: cores + extras. */
export function aplicarTemplate(t: Template): void {
  aplicarTemaNoRoot(t.cores);
  const root = document.documentElement;
  for (const [k, v] of Object.entries(t.extras)) {
    root.style.setProperty(k, v);
  }
  root.setAttribute('data-modo', t.modo);
  root.setAttribute('data-tema', t.id);
}
